import { useMemo, useState } from "react";

import { cn } from "@punchup/utils/cn";

type ExpandableTextProps = {
  text: string;
  /** How many lines to show when collapsed */
  wordsToShow?: number;
  /** Optional class names for the wrapper */
  className?: string;
  /** Optional class names for the text */
  textClassname?: string;
  /** Optional max paragraphs to show */
  limitParagraphsToShow?: number;
};

export default function ExpandableText({
  text,
  wordsToShow = 50,
  className = "",
  textClassname = "",
  limitParagraphsToShow = 99,
}: ExpandableTextProps) {
  const [expanded, setExpanded] = useState(false);

  const isOverflowing = useMemo(() => {
    return text.length > wordsToShow;
  }, [text, wordsToShow]);

  const textToShow = useMemo(() => {
    if (expanded) {
      return text;
    }
    const words = text.split(" ");
    let out =
      words.slice(0, wordsToShow).join(" ") + (isOverflowing ? "..." : "");

    if (out.split("\n").length > limitParagraphsToShow) {
      out = out.split("\n").slice(0, limitParagraphsToShow).join("\n") + "...";
    }

    return out;
  }, [text, wordsToShow, isOverflowing, expanded, limitParagraphsToShow]);

  const textLines = textToShow.split("\n");
  return (
    <div className={className}>
      {textLines.map((line, index) => (
        <p key={index} className={cn("mb-2", textClassname)}>
          {line}{" "}
          {textLines.length - 1 === index && isOverflowing ? (
            <>
              {expanded && <br />}
              <a
                href="javascript:void(0)"
                className="mt-2 text-sm no-underline hover:underline font-bold"
                aria-expanded={expanded}
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? "See less" : "See more"}
              </a>
            </>
          ) : null}
        </p>
      ))}
    </div>
  );
}
